/**
 * Field classification utilities for business view columns.
 * Pure functions — no React dependency.
 */

import type { DatasetViewColumnItem } from '../../api/dataModelsService';

// ─── Types ───────────────────────────────────────────────────────────────────

export type FieldCategory = 'all' | 'dimension' | 'measure' | 'date' | 'calculated';

// ─── Table name formatting ───────────────────────────────────────────────────

export function formatTableName(raw: string): string {
  return raw
    .replace(/_view$/i, '')
    .replace(/_table$/i, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Column classification ───────────────────────────────────────────────────

export function classifyColumn(col: DatasetViewColumnItem): FieldCategory {
  if (col.is_calculated) return 'calculated';
  const st = (col.semantic_type || '').toLowerCase();
  if (st.includes('measure') || st.includes('metric')) return 'measure';
  if (st.includes('date') || st.includes('time') || st.includes('temporal')) return 'date';
  return 'dimension';
}

export function categoryLabel(cat: FieldCategory): string {
  switch (cat) {
    case 'measure': return 'Measure';
    case 'date': return 'Date';
    case 'calculated': return 'Calculated';
    case 'dimension': return 'Dimension';
    default: return 'Unknown';
  }
}

export function categoryBadgeColor(cat: FieldCategory): 'subtle' | 'success' | 'informative' | 'important' {
  switch (cat) {
    case 'measure': return 'success';
    case 'date': return 'informative';
    case 'calculated': return 'important';
    case 'dimension':
    default: return 'subtle';
  }
}

export function dataTypeLabel(col: DatasetViewColumnItem): string {
  if (col.is_calculated) return 'Formula';
  const t = (col.type || '').toLowerCase();
  if (t.includes('int') || t.includes('float') || t.includes('double') || t.includes('decimal') || t.includes('number') || t.includes('numeric')) return 'Number';
  if (t.includes('date') || t.includes('time') || t.includes('timestamp')) return 'Datetime';
  if (t.includes('bool')) return 'Boolean';
  return 'String';
}

// ─── Field descriptions ──────────────────────────────────────────────────────

/** Generate a short fallback description based on field name and category */
function generateFallbackDescription(name: string, cat: FieldCategory): string {
  const display = name
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .trim()
    .toLowerCase();
  switch (cat) {
    case 'date':
      if (display.includes('created')) return 'Date when the record was created.';
      if (display.includes('closed') || display.includes('close')) return 'Date when the record was closed.';
      if (display.includes('converted')) return 'Date when the lead was converted.';
      if (display.includes('modified') || display.includes('updated')) return 'Date when the record was last modified.';
      return `Date field: ${display}.`;
    case 'measure':
      return `Numeric measure: ${display}.`;
    case 'calculated':
      return 'Calculated field.';
    case 'dimension':
    default:
      if (display.includes('owner') || display.includes('user')) return 'User responsible for the record.';
      if (display.includes('industry')) return 'Industry classification.';
      if (display.includes('source')) return 'Origin or source of the record.';
      if (display.includes('stage')) return 'Current stage in the process.';
      if (display.includes('status')) return 'Current status of the record.';
      if (display.includes('name')) return 'Name identifier.';
      return '';
  }
}

export function fieldDescription(col: DatasetViewColumnItem): string {
  if (col.is_calculated) {
    return col.formula_description || col.formula || 'Calculated field';
  }
  const cat = classifyColumn(col);
  if (cat === 'measure' && col.default_aggregation) {
    return `${col.default_aggregation.toUpperCase()} aggregation`;
  }
  return generateFallbackDescription(col.display_name || col.name, cat);
}